import React, { useState } from 'react';
import { Download, FileText, Calendar, Building, FileUser, ClipboardList, AlertCircle } from "lucide-react";
import jsPDF from 'jspdf';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import TenderRegistrationForm from "@/components/TenderRegistrationForm";
import SupplierRegistrationForm from "@/components/SupplierRegistrationForm";

const Tenders = () => {
  const [showTenderForm, setShowTenderForm] = useState(false);
  const [showSupplierForm, setShowSupplierForm] = useState(false);

  const generateTenderDocument = () => {
    const pdf = new jsPDF();
    const pageWidth = pdf.internal.pageSize.width;
    let yPosition = 20;

    // Page 1 - Header and Sections I & II
    // Main Header
    pdf.setFontSize(16);
    pdf.setFont(undefined, 'bold');
    pdf.setTextColor(0, 0, 0);
    pdf.text('TENDER DOCUMENT', 20, yPosition);
    yPosition += 15;

    // REQUEST FOR QUOTATION and Tender Number
    pdf.setFontSize(12);
    pdf.setFont(undefined, 'normal');
    pdf.text('REQUEST FOR QUOTATION', 20, yPosition);
    pdf.text('Tender No.: SACCO/ICT/01/2025', 20, yPosition + 10);
    yPosition += 20;

    // Title
    pdf.setFontSize(11);
    pdf.setFont(undefined, 'normal');
    pdf.text('Tender for Provision of Website Maintenance and Support Services', 20, yPosition);
    yPosition += 15;

    // Issued by section
    pdf.setFont(undefined, 'normal');
    pdf.text('Issued by:', 20, yPosition);
    yPosition += 8;
    pdf.text('NATCON STAFF SACCO LTD', 20, yPosition);
    yPosition += 8;
    pdf.text('P.O. Box 21046-00100', 20, yPosition);
    yPosition += 8;
    pdf.text('Nairobi, Kenya', 20, yPosition);
    yPosition += 15;

    // Dates in red
    pdf.setTextColor(255, 0, 0); // Red color
    pdf.text('Date of Issue: 4th July 2025', 20, yPosition);
    yPosition += 8;
    pdf.text('Closing Date & Time: 14th July 2025', 20, yPosition);
    yPosition += 15;

    // Reset to black
    pdf.setTextColor(0, 0, 0);

    // SECTION I: INVITATION TO TENDER
    pdf.setFont(undefined, 'bold');
    pdf.text('SECTION I: INVITATION TO TENDER', 20, yPosition);
    yPosition += 12;

    pdf.setFont(undefined, 'normal');
    pdf.text('SACCO Ltd invites proposals from pre-qualified suppliers under the Access to Government', 20, yPosition);
    yPosition += 6;
    pdf.text('Procurement Opportunities (AGPO) programme for the provision of website maintenance and', 20, yPosition);
    yPosition += 6;
    pdf.text('support services.', 20, yPosition);
    yPosition += 10;

    pdf.text('The successful provider will enter into a two-year service agreement, renewable subject to', 20, yPosition);
    yPosition += 6;
    pdf.text('performance.', 20, yPosition);
    yPosition += 15;

    // SECTION II: GENERAL INSTRUCTIONS TO BIDDERS
    pdf.setFont(undefined, 'bold');
    pdf.text('SECTION II: GENERAL INSTRUCTIONS TO BIDDERS', 20, yPosition);
    yPosition += 12;

    pdf.setFont(undefined, 'normal');
    pdf.text('1. The tender is open to firms legally registered in Kenya with proven experience in website', 20, yPosition);
    yPosition += 6;
    pdf.text('   development and maintenance.', 20, yPosition);
    yPosition += 10;

    pdf.text('2. Bidders should submit:', 20, yPosition);
    yPosition += 8;
    pdf.text('• Certificate of Registration/Business Incorporation.', 30, yPosition);
    yPosition += 6;
    pdf.text('• KRA PIN & Tax Compliance Certificate.', 30, yPosition);
    yPosition += 6;
    pdf.text('• Valid AGPO Certificate (Youth/Women/PWD).', 30, yPosition);
    yPosition += 6;
    pdf.text('• At least three (3) reference letters from similar assignments.', 30, yPosition);
    yPosition += 6;
    pdf.text('• Company profile (including key technical staff).', 30, yPosition);
    yPosition += 10;

    pdf.text('3. Proposals must be submitted in two separate envelopes:', 20, yPosition);
    yPosition += 8;
    pdf.text('• Technical Proposal (company qualifications, methodology, staff profiles,', 30, yPosition);
    yPosition += 6;
    pdf.text('  references).', 30, yPosition);
    yPosition += 6;
    pdf.text('• Financial Proposal (price schedule, inclusive of all taxes).', 30, yPosition);
    yPosition += 10;

    // Check if we're running out of space on page 1
    if (yPosition > 240) {
      pdf.addPage();
      yPosition = 20;
    }

    pdf.text('4. Proposals must be delivered in a plain sealed envelope marked:', 20, yPosition);
    yPosition += 8;
    pdf.text('"Tender No. NATCON/ICT/01/2025 – Website Maintenance"', 25, yPosition);
    yPosition += 8;
    pdf.text('and addressed to:', 20, yPosition);
    yPosition += 10;

    pdf.text('The Chairman', 25, yPosition);
    yPosition += 6;
    pdf.text('SACCO Ltd', 25, yPosition);
    yPosition += 6;
    pdf.text('P.O. Box 21046-00100', 25, yPosition);
    yPosition += 6;
    pdf.text('Nairobi', 25, yPosition);
    yPosition += 8;
    pdf.text('Or via email to: tenders@natconsacco.or.ke', 25, yPosition);
    yPosition += 10;

    pdf.text('5. Late bids will not be considered.', 20, yPosition);

    // Page 2 - SECTION III: SCOPE OF SERVICES
    pdf.addPage();
    yPosition = 20;

    pdf.setFont(undefined, 'bold');
    pdf.text('SECTION III: SCOPE OF SERVICES', 20, yPosition);
    yPosition += 10;

    pdf.setFont(undefined, 'normal');
    pdf.text('The contractor will provide the following:', 20, yPosition);
    yPosition += 12;

    // Scope items with detailed text from images
    pdf.setFont(undefined, 'bold');
    pdf.text('1. Website Audit & Strategy', 20, yPosition);
    yPosition += 8;
    pdf.setFont(undefined, 'normal');
    pdf.text('• Conduct a comprehensive review of the existing SACCO website.', 25, yPosition);
    yPosition += 6;
    pdf.text('• Assess performance, speed, accessibility, and member user experience.', 25, yPosition);
    yPosition += 6;
    pdf.text('• Provide a strategy roadmap for improvements aligned with SACCO\'s strategic plan and', 25, yPosition);
    yPosition += 6;
    pdf.text('  SASRA disclosure requirements.', 25, yPosition);
    yPosition += 10;

    pdf.setFont(undefined, 'bold');
    pdf.text('2. User Experience (UX) & User Interface (UI) Design', 20, yPosition);
    yPosition += 8;
    pdf.setFont(undefined, 'normal');
    pdf.text('• Redesign the website to be modern, professional, and member-friendly.', 25, yPosition);
    yPosition += 6;
    pdf.text('• Ensure intuitive navigation and logical placement of SACCO information (loans,', 25, yPosition);
    yPosition += 6;
    pdf.text('  savings, tenders, disclosures, reports).', 25, yPosition);
    yPosition += 10;

    pdf.setFont(undefined, 'bold');
    pdf.text('3. Content Management System (CMS) Integration', 20, yPosition);
    yPosition += 8;
    pdf.setFont(undefined, 'normal');
    pdf.text('• Implement a robust and secure CMS (e.g., WordPress, Drupal, or equivalent).', 25, yPosition);
    yPosition += 6;
    pdf.text('• Ensure easy updates by SACCO staff without requiring coding skills.', 25, yPosition);
    yPosition += 6;
    pdf.text('• Include role-based access controls.', 25, yPosition);
    yPosition += 10;

    pdf.setFont(undefined, 'bold');
    pdf.text('4. Responsive Web Design', 20, yPosition);
    yPosition += 8;
    pdf.setFont(undefined, 'normal');
    pdf.text('• Optimize the website for desktop, mobile, and tablet devices.', 25, yPosition);
    yPosition += 6;
    pdf.text('• Guarantee fast loading times and accessibility for all users, including persons with', 25, yPosition);
    yPosition += 6;
    pdf.text('  disabilities.', 25, yPosition);
    yPosition += 10;

    pdf.setFont(undefined, 'bold');
    pdf.text('5. Regulatory Disclosures & Member Communication', 20, yPosition);
    yPosition += 8;
    pdf.setFont(undefined, 'normal');
    pdf.text('• Create sections for mandatory SACCO disclosures (annual reports, audited accounts,', 25, yPosition);
    yPosition += 6;
    pdf.text('  Board notices, AGM information).', 25, yPosition);
    yPosition += 6;
    pdf.text('• Enable a member communication portal (downloads, notices, forms).', 25, yPosition);
    yPosition += 10;

    pdf.setFont(undefined, 'bold');
    pdf.text('6. Search Engine Optimization (SEO)', 20, yPosition);
    yPosition += 8;
    pdf.setFont(undefined, 'normal');
    pdf.text('• Optimize website structure and content to improve search visibility.', 25, yPosition);
    yPosition += 6;
    pdf.text('• Integrate analytics (Google Analytics or equivalent) for tracking traffic and user', 25, yPosition);
    yPosition += 6;
    pdf.text('  behavior.', 25, yPosition);
    yPosition += 10;

    pdf.setFont(undefined, 'bold');
    pdf.text('7. Security Enhancements', 20, yPosition);
    yPosition += 8;
    pdf.setFont(undefined, 'normal');
    pdf.text('• Install and manage SSL certificates.', 25, yPosition);
    yPosition += 6;
    pdf.text('• Ensure compliance with Kenya Data Protection Act, 2019 and SACCO ICT Risk', 25, yPosition);
    yPosition += 6;
    pdf.text('  Management Guidelines.', 25, yPosition);
    yPosition += 6;
    pdf.text('• Implement firewalls, malware protection, and regular security audits.', 25, yPosition);
    yPosition += 6;
    pdf.text('• Monitoring uptime and system performance.', 25, yPosition);
    yPosition += 10;

    // Check if we're running out of space on page 2 for third party integrations
    if (yPosition > 250) {
      pdf.addPage();
      yPosition = 20;
    }

    pdf.setFont(undefined, 'bold');
    pdf.text('8. Third-Party Integrations', 20, yPosition);
    yPosition += 8;
    pdf.setFont(undefined, 'normal');
    pdf.text('• Provide options for seamless integration with:', 25, yPosition);
    yPosition += 8;
    pdf.text('• SACCO Core Banking System (CBS)', 35, yPosition);
    yPosition += 6;
    pdf.text('• Customer Relationship Management (CRM)', 35, yPosition);
    yPosition += 6;
    pdf.text('• Payment Gateways (MPesa Paybill/Till, bank integrations)', 35, yPosition);
    yPosition += 6;
    pdf.text('• Electronic Document Management Systems (EDMS)', 35, yPosition);
    yPosition += 6;
    pdf.text('• Digital Marketing Tools (social media, newsletters, bulk SMS).', 35, yPosition);
    yPosition += 10;

    pdf.setFont(undefined, 'bold');
    pdf.text('9. Training & Knowledge Transfer', 20, yPosition);
    yPosition += 8;
    pdf.setFont(undefined, 'normal');
    pdf.text('• Provide hands-on training to SACCO staff on CMS use.', 25, yPosition);
    yPosition += 6;
    pdf.text('• Deliver user manuals and system documentation.', 25, yPosition);

    // Page 3 - Continue SECTION III and start SECTION IV
    pdf.addPage();
    yPosition = 20;

    pdf.setFont(undefined, 'bold');
    pdf.text('10. Routine Maintenance & Support', 20, yPosition);
    yPosition += 8;
    pdf.setFont(undefined, 'normal');
    pdf.text('• Offer post-launch support, updates, bug fixes, and performance monitoring.', 25, yPosition);
    yPosition += 6;
    pdf.text('• Provide 24/7 technical support via helpdesk (email/phone).', 25, yPosition);
    yPosition += 6;
    pdf.text('• Generate monthly maintenance and performance reports.', 25, yPosition);
    yPosition += 6;
    pdf.text('• Routine updates of content (news, notices, tenders, circulars).', 25, yPosition);
    yPosition += 6;
    pdf.text('• Monthly website backups and restorations as needed.', 25, yPosition);
    yPosition += 6;
    pdf.text('• Management of hosting and domain renewals.', 25, yPosition);
    yPosition += 12;

    pdf.setFont(undefined, 'bold');
    pdf.text('11. Ownership & Handover (Project Closure)', 20, yPosition);
    yPosition += 8;
    pdf.setFont(undefined, 'normal');
    pdf.text('• Provision of all source codes, files, system documentation, and credentials.', 25, yPosition);
    yPosition += 6;
    pdf.text('• Transfer of full intellectual property and ownership rights to NATCON Staff SACCO', 25, yPosition);
    yPosition += 6;
    pdf.text('  Ltd.', 25, yPosition);
    yPosition += 6;
    pdf.text('• Confirmation that no proprietary restrictions prevent future migration or vendor', 25, yPosition);
    yPosition += 6;
    pdf.text('  change.', 25, yPosition);
    yPosition += 15;

    // SECTION IV: EVALUATION CRITERIA
    pdf.setFont(undefined, 'bold');
    pdf.text('SECTION IV: EVALUATION CRITERIA', 20, yPosition);
    yPosition += 12;

    pdf.setFont(undefined, 'bold');
    pdf.text('Stage 1: Mandatory (Pass/Fail)', 20, yPosition);
    yPosition += 8;
    pdf.setFont(undefined, 'normal');
    pdf.text('• Certificate of Registration/Business Incorporation.', 25, yPosition);
    yPosition += 6;
    pdf.text('• KRA PIN & Tax Compliance Certificate.', 25, yPosition);
    yPosition += 6;
    pdf.text('• Valid AGPO Certificate (Youth/Women/PWD).', 25, yPosition);
    yPosition += 6;
    pdf.text('• At least three (3) reference letters from similar assignments.', 25, yPosition);
    yPosition += 6;
    pdf.text('• Company profile (including key technical staff).', 25, yPosition);
    yPosition += 12;

    pdf.setFont(undefined, 'bold');
    pdf.text('Stage 2: Technical Evaluation (70 Marks)', 20, yPosition);
    yPosition += 8;
    pdf.setFont(undefined, 'normal');
    pdf.text('• Relevant experience (5 assignments) – 20 marks.', 25, yPosition);
    yPosition += 6;
    pdf.text('• Team qualifications (ICT/web developers, security skills) – 15 marks.', 25, yPosition);
    yPosition += 6;
    pdf.text('• Approach, methodology, and work plan – 20 marks.', 25, yPosition);
    yPosition += 6;
    pdf.text('• Security and compliance measures – 15 marks.', 25, yPosition);
    yPosition += 12;

    pdf.setFont(undefined, 'bold');
    pdf.text('Stage 3: Financial Evaluation (30 Marks)', 20, yPosition);
    yPosition += 8;
    pdf.setFont(undefined, 'normal');
    pdf.text('• Evaluated on competitiveness, value for money, and clarity of costs.', 25, yPosition);
    yPosition += 15;

    // SECTION V: TERMS OF CONTRACT
    pdf.setFont(undefined, 'bold');
    pdf.text('SECTION V: TERMS OF CONTRACT', 20, yPosition);
    yPosition += 12;

    pdf.setFont(undefined, 'normal');
    pdf.text('1. Contract Period: Two (2) years, renewable based on performance.', 20, yPosition);
    yPosition += 8;
    pdf.text('2. Payments: Quarterly, upon submission of satisfactory maintenance reports.', 20, yPosition);
    yPosition += 8;
    pdf.text('3. Confidentiality: Contractor shall handle SACCO data securely and confidentially.', 20, yPosition);
    yPosition += 8;
    pdf.text('4. Termination: Either party may terminate with 60 days\' notice in writing.', 20, yPosition);
    yPosition += 8;
    pdf.text('5. Dispute Resolution: In case of disputes, parties shall first seek amicable settlement, failing', 20, yPosition);
    yPosition += 6;
    pdf.text('   which, the SACCO By-Laws and Kenyan law shall apply.', 20, yPosition);

    // Page 4 - SECTION VI & VII
    pdf.addPage();
    yPosition = 20;

    // SECTION VI: FINANCIAL PROPOSAL TEMPLATE
    pdf.setFont(undefined, 'bold');
    pdf.text('SECTION VI: FINANCIAL PROPOSAL TEMPLATE', 20, yPosition);
    yPosition += 15;

    // Create table with borders
    const tableStartX = 20;
    const tableWidth = 170;
    const colWidths = [70, 30, 35, 35];
    const tableStartY = yPosition;
    
    // Draw table borders
    pdf.rect(tableStartX, tableStartY, tableWidth, 60); // Main table border
    
    // Draw column lines
    let currentX = tableStartX;
    for (let i = 0; i < colWidths.length - 1; i++) {
      currentX += colWidths[i];
      pdf.line(currentX, tableStartY, currentX, tableStartY + 60);
    }
    
    // Draw header row separator
    pdf.line(tableStartX, tableStartY + 10, tableStartX + tableWidth, tableStartY + 10);
    
    // Draw row separators
    for (let i = 1; i <= 6; i++) {
      const rowY = tableStartY + 10 + (i * 8);
      pdf.line(tableStartX, rowY, tableStartX + tableWidth, rowY);
    }
    
    // Table headers
    pdf.setFont(undefined, 'bold');
    pdf.text('Item', tableStartX + 2, tableStartY + 7);
    pdf.text('Frequency', tableStartX + colWidths[0] + 2, tableStartY + 7);
    pdf.text('Unit Price (Kshs)', tableStartX + colWidths[0] + colWidths[1] + 2, tableStartY + 7);
    pdf.text('Total (Kshs)', tableStartX + colWidths[0] + colWidths[1] + colWidths[2] + 2, tableStartY + 7);
    
    // Table rows
    pdf.setFont(undefined, 'normal');
    const tableRows = [
      ['Domain & Hosting management', 'Annual'],
      ['SSL & Security updates', 'Annual'],
      ['Routine updates & backups', 'Monthly'],
      ['24/7 Technical support', 'Monthly'],
      ['Staff training (CMS)', 'Per session'],
      ['Annual website audit', 'Annual']
    ];

    tableRows.forEach((row, index) => {
      const rowY = tableStartY + 18 + (index * 8);
      pdf.text(row[0], tableStartX + 2, rowY);
      pdf.text(row[1], tableStartX + colWidths[0] + 2, rowY);
    });

    yPosition = tableStartY + 70;
    pdf.setFont(undefined, 'bold');
    pdf.text('TOTAL (Inclusive of VAT)', tableStartX + 2, yPosition);
    yPosition += 15;

    // SECTION VII: FORM OF PROPOSAL
    pdf.setFont(undefined, 'bold');
    pdf.text('SECTION VII: FORM OF PROPOSAL', 20, yPosition);
    yPosition += 15;

    pdf.setFont(undefined, 'normal');
    pdf.text('We, the undersigned, having examined the terms of reference, offer to provide website', 20, yPosition);
    yPosition += 6;
    pdf.text('maintenance services for SACCO Ltd at a total cost of Kshs __________ (amount in words)', 20, yPosition);
    yPosition += 6;
    pdf.text('inclusive of all taxes.', 20, yPosition);
    yPosition += 15;

    pdf.text('Firm Name: __________________________', 20, yPosition);
    yPosition += 10;
    pdf.text('Address: ____________________________', 20, yPosition);
    yPosition += 10;
    pdf.text('Authorized Signatory: ________________', 20, yPosition);
    yPosition += 10;
    pdf.text('Date: _______________________________', 20, yPosition);
    yPosition += 10;
    pdf.text('Official Stamp/Seal', 20, yPosition);

    // Download the PDF
    pdf.save('TENDER DOCUMENT.pdf');
  };

  const generateBlankTenderForm = () => {
    const pdf = new jsPDF();
    
    // Header with SACCO branding
    pdf.setFontSize(16);
    pdf.setTextColor(10, 111, 78); // Primary green color
    pdf.text('NATCON STAFF SACCO', 20, 20);
    
    pdf.setFontSize(10);
    pdf.setTextColor(0, 0, 0);
    pdf.text('KCB Towers, Kenya Road', 140, 15);
    pdf.text('P.O. Box 21046-00100', 140, 20);
    pdf.text('Tel: +254111485951', 140, 25);
    pdf.text('Email: natconsacco@nca.go.ke', 140, 30);
    
    // Title
    pdf.setFontSize(14);
    pdf.setTextColor(10, 111, 78);
    pdf.text('TENDER REGISTRATION FORM', 20, 45);
    
    let yPosition = 60;
    
    // Form fields with blank spaces
    pdf.setFontSize(10);
    pdf.setTextColor(0, 0, 0);
    
    const fields = [
      'TENDER DETAILS',
      'Tender Number: _________________________________',
      'Tender Title: _________________________________',
      'Category: _________________________________',
      'Lot (if applicable): _________________________________',
      '',
      'BIDDER INFORMATION',
      'Legal Entity Name: _________________________________',
      'Trading Name: _________________________________',
      'Entity Type: _________________________________',
      'Registration/Inc. No.: _________________________________',
      'KRA PIN: _________________________________',
      'VAT Status: _________________________________',
      'Primary Contact Person: _________________________________',
      'Contact Email: _________________________________',
      'Contact Phone: _________________________________',
      'Physical/Postal Address: _________________________________',
      '',
      'ELIGIBILITY AND PREFERENCE',
      'AGPO Category: _________________________________',
      'AGPO Certificate No.: _________________________________',
      '',
      'BENEFICIAL OWNERSHIP',
      'Beneficial Owners Summary:',
      '_________________________________________________',
      '_________________________________________________',
      '_________________________________________________',
      '',
      'DECLARATIONS',
      '☐ I/We declare that the information provided is true and accurate',
      '☐ I/We confirm no conflicts of interest exist',
      '☐ I/We consent to data processing for procurement purposes',
      '',
      'AUTHORIZATION',
      'Authorized Signatory: _________________________________',
      'Designation: _________________________________',
      'Date: _________________________________',
      'Signature: _________________________________'
    ];
    
    fields.forEach((field) => {
      if (yPosition > 270) {
        pdf.addPage();
        yPosition = 20;
      }
      
      if (field === '' || field.includes('DETAILS') || field.includes('INFORMATION') || field.includes('PREFERENCE') || field.includes('OWNERSHIP') || field.includes('DECLARATIONS') || field.includes('AUTHORIZATION')) {
        if (field !== '') {
          pdf.setFont(undefined, 'bold');
          pdf.text(field, 20, yPosition);
          yPosition += 10;
        } else {
          yPosition += 5;
        }
      } else {
        pdf.setFont(undefined, 'normal');
        pdf.text(field, 20, yPosition);
        yPosition += 8;
      }
    });
    
    // Download the PDF
    pdf.save('tender_registration_form_blank.pdf');
  };

  const generateBlankSupplierForm = () => {
    const pdf = new jsPDF();
    
    // Header with SACCO branding
    pdf.setFontSize(16);
    pdf.setTextColor(10, 111, 78); // Primary green color
    pdf.text('NATCON STAFF SACCO', 20, 20);
    
    pdf.setFontSize(10);
    pdf.setTextColor(0, 0, 0);
    pdf.text('KCB Towers, Kenya Road', 140, 15);
    pdf.text('P.O. Box 21046-00100', 140, 20);
    pdf.text('Tel: +254111485951', 140, 25);
    pdf.text('Email: natconsacco@nca.go.ke', 140, 30);
    
    // Title
    pdf.setFontSize(14);
    pdf.setTextColor(10, 111, 78);
    pdf.text('SUPPLIER REGISTRATION FORM', 20, 45);
    
    let yPosition = 60;
    
    // Form fields with blank spaces
    pdf.setFontSize(10);
    pdf.setTextColor(0, 0, 0);
    
    const fields = [
      'COMPANY DETAILS',
      'Legal Entity Name: _________________________________',
      'Website: _________________________________',
      'Entity Type: _________________________________',
      'Registration/Inc. No.: _________________________________',
      'KRA PIN: _________________________________',
      'TCC Expiry Date: _________________________________',
      'Primary Contact Person: _________________________________',
      'Contact Email: _________________________________',
      'Contact Phone: _________________________________',
      'Physical/Postal Address: _________________________________',
      '',
      'SUPPLY CATEGORIES',
      '☐ ICT Equipment & Peripherals',
      '☐ Office Stationery & Supplies',
      '☐ Furniture & Fixtures',
      '☐ Printing & Branding',
      '☐ Security & Surveillance',
      '☐ Consultancy/Professional Services',
      '☐ Transport & Logistics',
      '☐ Building Maintenance & Utilities',
      '☐ Other',
      '',
      'BENEFICIAL OWNERSHIP',
      'Beneficial Owners Summary:',
      '_________________________________________________',
      '_________________________________________________',
      '_________________________________________________',
      '',
      'BANKING DETAILS',
      'Bank Name: _________________________________',
      'Bank Branch: _________________________________',
      'Account Name: _________________________________',
      'Account Number: _________________________________',
      'SWIFT/BIC Code: _________________________________',
      'M-Pesa Paybill: _________________________________',
      '',
      'CLIENT REFERENCES',
      '_________________________________________________',
      '_________________________________________________',
      '_________________________________________________',
      '',
      'DECLARATIONS',
      '☐ I/We declare compliance with ethical standards',
      '☐ I/We confirm HSE policy adherence (if applicable)',
      '☐ I/We consent to data processing',
      '',
      'AUTHORIZATION',
      'Authorized Signatory: _________________________________',
      'Designation: _________________________________',
      'Date: _________________________________',
      'Signature: _________________________________'
    ];
    
    fields.forEach((field) => {
      if (yPosition > 270) {
        pdf.addPage();
        yPosition = 20;
      }
      
      if (field === '' || field.includes('DETAILS') || field.includes('CATEGORIES') || field.includes('OWNERSHIP') || field.includes('BANKING') || field.includes('REFERENCES') || field.includes('DECLARATIONS') || field.includes('AUTHORIZATION')) {
        if (field !== '') {
          pdf.setFont(undefined, 'bold');
          pdf.text(field, 20, yPosition);
          yPosition += 10;
        } else {
          yPosition += 5;
        }
      } else {
        pdf.setFont(undefined, 'normal');
        pdf.text(field, 20, yPosition);
        yPosition += 8;
      }
    });
    
    // Download the PDF
    pdf.save('supplier_registration_form_blank.pdf');
  };

  return (
    <div className="min-h-screen">
      <Header />
      <main className="py-16">
        <div className="container max-w-6xl mx-auto px-4">
          {/* Page Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-foreground mb-4">Tenders & Disclosures</h1>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              A centralized hub for all NATCON Sacco's tender documents and public disclosures. 
              Stay informed about current opportunities and access closed tender documents.
            </p>
          </div>

          {/* Current Tenders Section */}
          <section className="mb-16">
            <Card className="bg-gradient-to-r from-orange-50 to-orange-100 border-orange-200">
              <CardHeader>
                <div className="flex items-center gap-3 mb-4">
                  <AlertCircle className="h-6 w-6 text-orange-600" />
                  <CardTitle className="text-2xl">Current Tenders</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-center py-8">
                  <FileText className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-foreground mb-2">No Open Tenders</h3>
                  <p className="text-muted-foreground">
                    No open tenders at this time. Please check back later for new opportunities.
                  </p>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Closed Tenders Section */}
          <section className="mb-16">
            <Card className="bg-gradient-to-r from-blue-50 to-blue-100 border-blue-200">
              <CardHeader>
                <div className="flex items-center gap-3 mb-4">
                  <FileText className="h-6 w-6 text-blue-600" />
                  <CardTitle className="text-2xl">Closed Tenders</CardTitle>
                </div>
                <p className="text-muted-foreground">
                  Below is the tender document for the provision of website maintenance and support services. This tender is now closed.
                </p>
              </CardHeader>
              <CardContent>
                <Card className="border-2 border-primary/20">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-lg text-primary">
                          Tender No.: SACCO/ICT/01/2025 - Website Maintenance and Support Services
                        </CardTitle>
                        <p className="text-sm text-muted-foreground mt-1">
                          Date of Issue: 4th July 2025 | Closing Date: 14th July 2025
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">Closed</span>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="bg-muted/50 p-4 rounded-lg">
                        <h4 className="font-medium text-foreground mb-2">Tender Overview</h4>
                        <p className="text-sm text-muted-foreground">
                          SACCO Ltd invited proposals from pre-qualified suppliers under the Access to Government 
                          Procurement Opportunities (AGPO) programme for the provision of website maintenance and 
                          support services. The successful provider entered into a two-year service agreement.
                        </p>
                      </div>
                      
                      <div className="grid md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <h5 className="font-medium text-foreground mb-2">Key Requirements:</h5>
                          <ul className="space-y-1 text-muted-foreground">
                            <li>• Website audit & strategy</li>
                            <li>• UX/UI design improvements</li>
                            <li>• CMS integration</li>
                            <li>• Responsive web design</li>
                            <li>• Security enhancements</li>
                          </ul>
                        </div>
                        <div>
                          <h5 className="font-medium text-foreground mb-2">Submission Requirements:</h5>
                          <ul className="space-y-1 text-muted-foreground">
                            <li>• Business registration documents</li>
                            <li>• Tax compliance certificates</li>
                            <li>• AGPO certificate</li>
                            <li>• Reference letters (minimum 3)</li>
                            <li>• Company profile</li>
                          </ul>
                        </div>
                      </div>
                      
                      <Separator />
                      
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-sm font-medium text-foreground">Complete Tender Document</p>
                          <p className="text-xs text-muted-foreground">TENDER DOCUMENT.pdf</p>
                        </div>
                        <Button variant="outline" onClick={generateTenderDocument} className="flex items-center gap-2">
                          <Download className="h-4 w-4" />
                          Download PDF
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </CardContent>
            </Card>
          </section>


          {/* Supplier Registration */}
          <section className="mb-16">
            <Card className="bg-gradient-to-r from-primary/5 to-primary/10 border-primary/20">
              <CardHeader>
                <div className="flex items-center gap-3 mb-4">
                  <Building className="h-6 w-6 text-primary" />
                  <CardTitle className="text-2xl">Supplier Prequalification</CardTitle>
                </div>
                <p className="text-muted-foreground">
                  Join our approved supplier database and be notified of relevant procurement opportunities. 
                  Registration ensures you receive timely updates on tenders matching your business capabilities.
                </p>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <h4 className="font-medium text-foreground mb-2">Requirements:</h4>
                      <ul className="space-y-1 text-muted-foreground">
                        <li>• Valid business registration documents</li>
                        <li>• Tax compliance certificates</li>
                        <li>• Company profile and references</li>
                        <li>• Financial statements (last 2 years)</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-medium text-foreground mb-2">Benefits:</h4>
                      <ul className="space-y-1 text-muted-foreground">
                        <li>• Priority notification of relevant tenders</li>
                        <li>• Streamlined bidding process</li>
                        <li>• Access to supplier portal</li>
                        <li>• Technical support and guidance</li>
                      </ul>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <Button size="lg" className="flex items-center gap-2" onClick={() => setShowSupplierForm(true)}>
                      <FileUser className="h-4 w-4" />
                      Register as a Supplier
                    </Button>
                    <Button size="lg" variant="outline" className="flex items-center gap-2" onClick={() => setShowTenderForm(true)}>
                      <ClipboardList className="h-4 w-4" />
                      Apply for Tender
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Download Forms Section */}
          <section className="mb-16">
            <Card className="bg-gradient-to-r from-secondary/50 to-muted/50">
              <CardHeader>
                <CardTitle className="text-xl flex items-center gap-3">
                  <Download className="h-5 w-5 text-primary" />
                  Download Forms
                </CardTitle>
                <p className="text-muted-foreground">
                  Download blank forms to fill offline and submit later via email or in person.
                </p>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  <Card className="border-primary/20">
                    <CardContent className="p-4">
                      <h4 className="font-semibold mb-2">Tender Registration Form</h4>
                      <p className="text-sm text-muted-foreground mb-4">
                        Complete form for applying to specific tenders. Includes all required fields for tender submissions.
                      </p>
                      <Button variant="outline" className="w-full" onClick={generateBlankTenderForm}>
                        <Download className="h-4 w-4 mr-2" />
                        Download
                      </Button>
                    </CardContent>
                  </Card>
                  <Card className="border-primary/20">
                    <CardContent className="p-4">
                      <h4 className="font-semibold mb-2">Supplier Registration Form</h4>
                      <p className="text-sm text-muted-foreground mb-4">
                        Register your company in our supplier database for future procurement opportunities.
                      </p>
                      <Button variant="outline" className="w-full" onClick={generateBlankSupplierForm}>
                        <Download className="h-4 w-4 mr-2" />
                        Download
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              </CardContent>
            </Card>
          </section>

          {/* Contact Information */}
          <section>
            <Card>
              <CardHeader>
                <CardTitle>Procurement Department Contact</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium text-foreground mb-2">For Tender Inquiries:</h4>
                    <p className="text-muted-foreground">
                      Email: natconsacco@nca.go.ke<br />
                      Phone: +254111485951<br />
                      Office Hours: Monday - Friday, 8:00 AM - 5:00 PM
                    </p>
                  </div>
                  <div>
                    <h4 className="font-medium text-foreground mb-2">Physical Address:</h4>
                    <p className="text-muted-foreground">
                      KCB Towers, Kenya Road<br />
                      Housed in National Construction Authority NCA<br />
                      P.O. Box 21046-00100
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>
        </div>
      </main>
      <Footer />
      
      {/* Form Dialogs */}
      <Dialog open={showTenderForm} onOpenChange={setShowTenderForm}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <TenderRegistrationForm onClose={() => setShowTenderForm(false)} />
        </DialogContent>
      </Dialog>
      
      <Dialog open={showSupplierForm} onOpenChange={setShowSupplierForm}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <SupplierRegistrationForm onClose={() => setShowSupplierForm(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Tenders;